/**
 * PDF Generator for Technical Sheets
 * Extracted from TechnicalSheets.js
 */
import { jsPDF } from "jspdf";
import { resolveImageUrl, formatCurrency, calcGramaturaTotalFromList } from "./sheetUtils";

export async function generateSheetPDF(sheet) {
  const doc = new jsPDF();
  const pageW = 210;
  const marginL = 15;
  const marginR = 195;
  const contentW = marginR - marginL;
  const footerY = 285;
  const maxContentY = 272;

  let yPos = 15;

  const checkPage = (needed) => {
    if (yPos + needed > maxContentY) {
      doc.addPage();
      yPos = 15;
    }
  };

  // === HEADER ===
  doc.setFontSize(16);
  doc.setFont(undefined, 'bold');
  doc.text('FICHA TÉCNICA', marginL, yPos);
  yPos += 8;

  doc.setFontSize(12);
  doc.text(sheet.name.toUpperCase(), marginL, yPos);
  yPos += 4;
  doc.setDrawColor(40, 40, 40);
  doc.setLineWidth(0.8);
  doc.line(marginL, yPos, marginR, yPos);
  yPos += 8;

  // === FOTO ===
  const renderInfo = () => {
    doc.setFontSize(9);
    doc.setFont(undefined, 'bold');
    doc.text(`Rendimento: ${sheet.yield_quantity} ${sheet.yield_unit}`, marginL, yPos); yPos += 5;
    if (sheet.gramatura_porcao) { doc.text(`Gramatura/Porção: ${sheet.gramatura_porcao} kg`, marginL, yPos); yPos += 5; }
    const gramTotal = calcGramaturaTotalFromList(sheet.ingredients);
    doc.text(`Peso Total: ${gramTotal.toFixed(3)} kg`, marginL, yPos); yPos += 5;
    doc.text(`Custo Total: ${formatCurrency(sheet.total_cost)}`, marginL, yPos); yPos += 5;
    doc.text(`Custo/Unidade: ${formatCurrency(sheet.cost_per_unit)}`, marginL, yPos); yPos += 8;
  };

  if (sheet.foto_url) {
    try {
      const imgUrl = resolveImageUrl(sheet.foto_url);
      const token = localStorage.getItem("token");
      const resp = await fetch(imgUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (resp.ok) {
        const blob = await resp.blob();
        const dataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });

        const img = await new Promise((resolve, reject) => {
          const i = new Image();
          i.onload = () => resolve(i);
          i.onerror = reject;
          i.src = dataUrl;
        });

        const maxImgW = 80;
        const maxImgH = 50;
        const ratio = Math.min(maxImgW / img.width, maxImgH / img.height);
        const imgW = img.width * ratio;
        const imgH = img.height * ratio;

        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        canvas.getContext('2d').drawImage(img, 0, 0);
        const jpegData = canvas.toDataURL('image/jpeg', 0.75);

        checkPage(imgH + 5);
        doc.addImage(jpegData, 'JPEG', marginL, yPos, imgW, imgH);

        let infoX = marginL + imgW + 8;
        let infoY = yPos + 2;
        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.text(`Rendimento: ${sheet.yield_quantity} ${sheet.yield_unit}`, infoX, infoY);
        infoY += 6;
        if (sheet.gramatura_porcao) {
          doc.text(`Gramatura/Porção: ${sheet.gramatura_porcao} kg`, infoX, infoY);
          infoY += 6;
        }
        const gramTotal = calcGramaturaTotalFromList(sheet.ingredients);
        doc.text(`Peso Total: ${gramTotal.toFixed(3)} kg`, infoX, infoY);
        infoY += 6;
        doc.text(`Custo Total: ${formatCurrency(sheet.total_cost)}`, infoX, infoY);
        infoY += 6;
        doc.text(`Custo/Unidade: ${formatCurrency(sheet.cost_per_unit)}`, infoX, infoY);

        yPos += imgH + 8;
      } else {
        throw new Error('fetch failed');
      }
    } catch (e) {
      console.warn("PDF image failed:", e.message);
      renderInfo();
    }
  } else {
    renderInfo();
  }

  // === INGREDIENTES ===
  checkPage(25);
  doc.setFontSize(10);
  doc.setFont(undefined, 'bold');
  doc.text('INGREDIENTES', marginL, yPos);
  yPos += 5;

  doc.setFillColor(35, 35, 35);
  doc.rect(marginL, yPos - 4, contentW, 7, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(7);
  doc.text('Ingrediente', marginL + 2, yPos);
  doc.text('Bruta', 100, yPos);
  doc.text('FC', 125, yPos);
  doc.text('Líquida', 140, yPos);
  doc.text('Custo', marginR - 2, yPos, { align: 'right' });
  doc.setTextColor(0, 0, 0);
  yPos += 6;

  doc.setFontSize(8);
  doc.setFont(undefined, 'normal');

  sheet.ingredients.forEach((ing, index) => {
    checkPage(8);

    if (index % 2 === 0) {
      doc.setFillColor(245, 245, 245);
      doc.rect(marginL, yPos - 4, contentW, 7, 'F');
    }

    const name = ing.ingredient_name.length > 35
      ? ing.ingredient_name.substring(0, 33) + '...'
      : ing.ingredient_name;

    if (ing.is_qb) {
      doc.text(name, marginL + 2, yPos);
      doc.setFont(undefined, 'italic');
      doc.text('QB', 100, yPos);
      doc.text('-', 125, yPos);
      doc.text('QB', 140, yPos);
      doc.text('-', marginR - 2, yPos, { align: 'right' });
      doc.setFont(undefined, 'normal');
    } else {
      doc.text(name, marginL + 2, yPos);
      doc.text(`${ing.quantidade_bruta} ${ing.unit}`, 100, yPos);
      doc.text(ing.fator_coccao.toFixed(2), 125, yPos);
      doc.text(`${ing.quantidade_liquida} ${ing.unit}`, 140, yPos);
      doc.text(formatCurrency(ing.item_cost), marginR - 2, yPos, { align: 'right' });
    }
    yPos += 7;
  });

  yPos += 2;
  doc.setLineWidth(0.5);
  doc.line(marginL, yPos, marginR, yPos);
  yPos += 6;
  doc.setFont(undefined, 'bold');
  doc.setFontSize(9);
  doc.text('TOTAL:', 140, yPos);
  doc.text(formatCurrency(sheet.total_cost), marginR - 2, yPos, { align: 'right' });
  yPos += 10;

  // === MODO DE PREPARO ===
  if (sheet.modo_preparo) {
    checkPage(15);
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text('MODO DE PREPARO', marginL, yPos);
    yPos += 6;

    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    const prepLines = doc.splitTextToSize(sheet.modo_preparo, contentW);
    prepLines.forEach(line => {
      checkPage(6);
      doc.text(line, marginL, yPos);
      yPos += 4.5;
    });
  }

  // === FOOTER ===
  const pageCount = doc.internal.pages.length - 1;
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.3);
    doc.line(marginL, footerY - 3, marginR, footerY - 3);
    doc.setFont(undefined, 'bold');
    doc.text('SOP & RAFT', marginL, footerY);
    doc.setFont(undefined, 'normal');
    doc.text(new Date().toLocaleDateString('pt-BR'), pageW / 2, footerY, { align: 'center' });
    doc.text(`${i}/${pageCount}`, marginR, footerY, { align: 'right' });
  }

  return doc;
}
